export type SendOption = "now" | "scheduled" | "optimal";

interface ZonedDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function parseLocalDateTime(localDateTime: string): ZonedDateParts {
  const match = localDateTime.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );

  if (!match) {
    throw new Error("Invalid local datetime");
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4]),
    minute: Number(match[5]),
    second: Number(match[6] ?? "0"),
  };
}

function formatLocalDateTime(parts: ZonedDateParts): string {
  const year = String(parts.year).padStart(4, "0");
  const month = String(parts.month).padStart(2, "0");
  const day = String(parts.day).padStart(2, "0");
  const hour = String(parts.hour).padStart(2, "0");
  const minute = String(parts.minute).padStart(2, "0");
  const second = String(parts.second).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

function addDays(parts: ZonedDateParts, days: number): ZonedDateParts {
  const next = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days, parts.hour, parts.minute, parts.second));
  return {
    year: next.getUTCFullYear(),
    month: next.getUTCMonth() + 1,
    day: next.getUTCDate(),
    hour: next.getUTCHours(),
    minute: next.getUTCMinutes(),
    second: next.getUTCSeconds(),
  };
}

function getZonedDateParts(date: Date, timeZone: string): ZonedDateParts {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const values = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );

  const year = values.year;
  const month = values.month;
  const day = values.day;
  const hour = values.hour;
  const minute = values.minute;
  const second = values.second;

  if (
    typeof year !== "number"
    || typeof month !== "number"
    || typeof day !== "number"
    || typeof hour !== "number"
    || typeof minute !== "number"
    || typeof second !== "number"
  ) {
    throw new Error("Failed to resolve timezone date parts");
  }

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
  };
}

function getTimeZoneOffsetMs(utcDate: Date, timeZone: string): number {
  const zoned = getZonedDateParts(utcDate, timeZone);
  const localAsUtc = Date.UTC(
    zoned.year,
    zoned.month - 1,
    zoned.day,
    zoned.hour,
    zoned.minute,
    zoned.second,
  );

  return localAsUtc - utcDate.getTime();
}

export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function listSupportedTimezones(): string[] {
  if (typeof Intl.supportedValuesOf === "function") {
    return Intl.supportedValuesOf("timeZone");
  }

  return ["UTC", getBrowserTimezone()];
}

export function convertZonedLocalToUtcIso(localDateTime: string, timeZone: string): string {
  const parts = parseLocalDateTime(localDateTime);
  const utcGuess = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  ));

  const offsetMs = getTimeZoneOffsetMs(utcGuess, timeZone);
  return new Date(utcGuess.getTime() - offsetMs).toISOString();
}

export function resolveOptimalSendUtc(
  optimalHour: number,
  timeZone: string,
  now: Date = new Date(),
): string {
  const nowInTimezone = getZonedDateParts(now, timeZone);
  let scheduledParts: ZonedDateParts = {
    ...nowInTimezone,
    hour: optimalHour,
    minute: 0,
    second: 0,
  };

  const currentIso = convertZonedLocalToUtcIso(formatLocalDateTime(nowInTimezone), timeZone);
  let scheduledIso = convertZonedLocalToUtcIso(formatLocalDateTime(scheduledParts), timeZone);

  if (new Date(scheduledIso).getTime() <= new Date(currentIso).getTime()) {
    scheduledParts = addDays(scheduledParts, 1);
    scheduledIso = convertZonedLocalToUtcIso(formatLocalDateTime(scheduledParts), timeZone);
  }

  return scheduledIso;
}

export function formatScheduledSummary(
  sendOption: SendOption,
  scheduledAt: string,
  timeZone: string,
  optimalHour?: number | null,
): string {
  if (sendOption === "now") {
    return "Immediately";
  }

  if (sendOption === "optimal" && optimalHour !== null && optimalHour !== undefined) {
    return `Next optimal window at ${String(optimalHour).padStart(2, "0")}:00 (${timeZone})`;
  }

  if (!scheduledAt) {
    return "Scheduled time pending";
  }

  const utc = convertZonedLocalToUtcIso(scheduledAt, timeZone);
  return `${new Date(utc).toLocaleString()} (${timeZone})`;
}
